/* eslint-disable max-lines */
import {action, computed, makeObservable, observable} from 'mobx';
import {updateOrAddObject} from 'app/utils/updateArraysObject';
import {isDesktop} from 'app/utils';
import logEvent from 'app/amplitude/log_event';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';
import {
    casinoGameById,
    favoriteGameChange,
    sendRequestStartCasino,
    loadGames,
    lastFindSame
} from 'app/services/CasinoServices';
import {
    CasinoGameInterface,
    FilteredParamsInterface,
    SortingsInterface,
    CasinoGamesStoreInterface,
    PlayGameParamsInterface,
    GameLaunchOptionsInterface,
    CasinoFilterInterface,
    CasinoProvidersInterface,
    CasinoGamesCategoriesInterface,
    BigWinInterface,
    LastGameInterface,
    PageElementsMap
} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import {toastError} from 'app/components/toasts/liteToast';
import {CasinoGamesController} from 'app/controllers/casinoGamesController';
import I18n from 'app/i18n';
import {RestApiService} from 'app/services/RestApiService';
import {CasinoGameUpdatePayloadInterface} from 'app/interfaces/CasinoGameUpdatePayloadInterface';
import CasinoLaunchInterface from 'app/interfaces/CasinoLaunchInterface';
import type {IframeGridPlacement} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingBlocksTypes';

export const GAMES_PER_PAGE = 40;
export const DEFAULT_SORTING = 'popular';
export const GAMES_FIND_LIMIT = 1000;

export const FILTER_GROUP_TYPES = {
    NAVIGATIOR_BAR: 0,
    POPULAR: 2,
    TOURNAMENT: 1
};

export class CasinoGamesStore {
    providerFilterStorageKey = 'casino-filter__section';

    controller: CasinoGamesController;

    constructor() {
        makeObservable(this);
        this.controller = new CasinoGamesController(this);
    }

    @observable data: CasinoGamesStoreInterface = {
        casinoFilters: [],
        casinoGames: [],
        casinoGamesByFiltersCount: 0,
        casinoGamesCategories: [],
        casinoProviders: [],
        totalGamesCount: 0,
        usersFavoritesGameCount: 0
    };

    @observable
    biggestWins: BigWinInterface[] = [];

    @observable
    lastGames: LastGameInterface[] = [];

    @observable
    pageElements: PageElementsMap = {};

    @observable
    homePageGames: CasinoGameInterface[] = [];

    @observable
    randomSlotGames: CasinoGameInterface[] = [];

    @observable
    casinoFilters: CasinoFilterInterface[] = [];

    @observable
    collectionSize = 0;

    @observable
    filters: CasinoFilterInterface[] = [];

    @observable
    favoritesGameCount = 0;

    @observable
    totalGamesCount = 0;

    @observable
    popularGamesFilter: CasinoFilterInterface | null = null;

    @observable
    casinoProviders: CasinoProvidersInterface[] = [];

    @observable
    casinoGamesCategories: CasinoGamesCategoriesInterface[] = [];

    @observable filteredParams: FilteredParamsInterface = {
        categories: [],
        favorite: false,
        find: '',
        game_id: null,
        provider: []
    };

    @observable selectedCategoriesIds: string[] = [];

    @observable activeCasinoGame?: CasinoGameInterface | null;

    @observable partsLoaded = 1;

    @observable hasMore = true;

    @observable sortingValue = DEFAULT_SORTING;

    @observable loadingNewGames = true;

    @observable showOnlyFavorite = false;

    @observable isGameActive = false;

    @observable iframeGridPlacement: IframeGridPlacement | null = null;

    @observable reloadPage = false;

    @observable gameLaunchOptions?: GameLaunchOptionsInterface = null;

    @observable selectedFilterGroupId = ELEMENT_NOT_FOUND;

    @observable lastFind: FilteredParamsInterface = {
        categories: [],
        favorite: false,
        find: '',
        game_id: null,
        provider: []
    };

    @observable sortings: SortingsInterface[] = [
        {id: 0, value: 'popular'},
        {id: 1, value: 'new'},
        {id: 2, value: 'alphabetical'}
    ];

    @computed
    get favoriteGames(): CasinoGameInterface[] {
        const {casinoGames} = this.data;

        return casinoGames.filter(game => game.favorite === true);
    }

    @computed
    get device(): string {
        return isDesktop() ? 'desktop' : 'mobile';
    }

    @action
    initialize = (): void => {
        const restoredProviderFilter = this.restoreSavedProviderFilter();

        const params = this.filteredParams;
        const queryParams = {
            device: this.device,
            filter_group: this.selectedFilterGroupId > 0
                ? this.selectedFilterGroupId
                : null,
            order_by: this.sortingValue,
            page: this.partsLoaded,
            ...params
        };

        if (restoredProviderFilter) {
            this.checkUpdateFilteredParams(true);
        } else {
            this.controller.loadGames(queryParams);
        }
        this.controller.loadFilters(queryParams);
        this.controller.init();
    };

    @action
    // eslint-disable-next-line max-statements
    init = (data: CasinoGamesStoreInterface): void => {
        this.update('data', {
            ...{casinoGamesByFiltersCount: data.casinoGamesByFiltersCount},
            ...{casinoGames: this.data.casinoGames}
        });
        this.updateBiggestWins(data.biggestWins);
        this.updateLastGame(data.lastGames);
        this.updatePageElements(data.pageElements);
        this.updateCasinoProviders(data.casinoProviders);
        this.updateCasinoGamesCategories(data.casinoGamesCategories);
        this.updateTabFilters(data.casinoFilters);
        this.setFavoriteGamesCount(data.usersFavoritesGameCount);
        this.setTotalGamesCount(data.totalGamesCount);
        this.setPopularGamesFilter(data.popularFilter ?? null);
        if (data.casinoGames?.length < GAMES_PER_PAGE) {
            this.changeHasMore(false);
        }
        if (data.random_slot_games?.length) {
            this.setRandomSlotGames(data.random_slot_games);
        }
    };

    @action
    updateBiggestWins = (wins: BigWinInterface[]): void => {
        this.biggestWins = wins;
    };

    @action
    updateLastGame = (games: LastGameInterface[]): void => {
        this.lastGames = games;
    };

    @action
    updatePageElements = (elements: PageElementsMap | undefined): void => {
        this.pageElements = elements ?? {};
    };

    @action
    updatePageElementsGame = (game: CasinoGameInterface): void => {
        const hasMatch = Object.values(this.pageElements).some(el => el.casino_game?.id === game.id);

        if (!hasMatch) {
            return;
        }

        this.pageElements = Object.fromEntries(
            Object.entries(this.pageElements).map(([key, el]) => {
                if (el.casino_game?.id !== game.id) {
                    return [key, el];
                }
                return [key, {...el, casino_game: {...el.casino_game, favorite: game.favorite}}];
            })
        );
    };

    @action
    update = (variableName: string, data: CasinoGamesStoreInterface): void => {
        this[variableName] = data;
    };

    @action
    setFavoriteGamesCount = (count: number): void => {
        this.favoritesGameCount = count;
    };

    @action
    setTotalGamesCount = (count: number): void => {
        this.totalGamesCount = count;
    };

    @action
    setPopularGamesFilter = (filter: CasinoFilterInterface | null): void => {
        this.popularGamesFilter = filter;
    };

    @action
    updateCasinoProviders = (providers: CasinoProvidersInterface[]): void => {
        this.casinoProviders = [...providers];
    };

    @action
    updateCasinoGamesCategories = (gamesCategories: CasinoGamesCategoriesInterface[]): void => {
        this.casinoGamesCategories = [...gamesCategories];
    };

    @action
    updateTabFilters(filters: CasinoFilterInterface[]): void {
        this.filters = [];
        this.filters = this.filters.concat(filters);
    }

    @action
    updateElementCollection(item: CasinoGameInterface): void {
        updateOrAddObject(this.data, 'casinoGames', item);
        if (this.activeCasinoGame && this.activeCasinoGame.id === item.id) {
            this.changeActiveGame(item);
        }
        if (this.showOnlyFavorite) {
            this.updateGamesCollection(
                this.data.casinoGames.filter(game => game.favorite === true)
            );
        }
    }

    @action
    updateGamesCollection(casinoGames: CasinoGameInterface[]): void {
        this.data.casinoGames = casinoGames;
    }

    @action
    updateGames(data: { casinoGames: CasinoGameInterface[]; random_slot_games?: CasinoGameInterface[] }): void {
        if (this) {
            this.update('data', {casinoGames: []});
            const filteredCasinoGames = data.casinoGames?.filter(game => game.devices.includes(this.device));
            this.update('data', {...{casinoGames: this.data.casinoGames.concat(filteredCasinoGames)}});
            if (data.random_slot_games?.length) {
                this.setRandomSlotGames(data.random_slot_games);
            }
        }
    }

    @action
    setRandomSlotGames(games: CasinoGameInterface[]): void {
        this.randomSlotGames = games;
    }

    @action
    partsChanged(partsLoaded: number): void {
        this.partsLoaded = partsLoaded;
    }

    @action
    changeHasMore(hasMore: boolean): void {
        this.hasMore = hasMore;
    }

    @action
    updateShowOnlyFavorite(showOnlyFavorite: boolean): void {
        this.showOnlyFavorite = showOnlyFavorite;
        this.filteredParams.favorite = showOnlyFavorite;
        this.filteredParams.provider = [];
        this.saveProviderFilter([]);
    }

    @action
    updateLastFind(lastFind: FilteredParamsInterface): void {
        this.lastFind = lastFind;
    }

    @action
    changeSortingValue(value: string): void {
        this.sortingValue = value;
    }

    @action
    selectFilterGroup(id: number): void {
        this.selectedFilterGroupId = id;
    }

    @action
    updateGameId = (gameId: string): void => {
        this.filteredParams.game_id = gameId;
    };

    @action
    updateFindFilteredParams = (find: string, gameId: string): void => {
        this.filteredParams.find = find;
        this.filteredParams.game_id = gameId;
        this.filteredParams.favorite = false;
        this.showOnlyFavorite = false;
        this.selectFilterGroup(null);
        this.checkUpdateFilteredParams();
    };

    @action
    updateFilteredParams(identifier: string, value: string | boolean | number): void {
        this.filteredParams[identifier] = value;
        this.checkUpdateFilteredParams();
    }

    @action
    updateSelectProviders = (ids: string[]): void => {
        this.filteredParams.provider = ids;
        this.saveProviderFilter(ids);
    };

    @action
    updateSelectCategories = (categories: string[], selectedCategoriesIds: string[]): void => {
        this.selectedCategoriesIds = selectedCategoriesIds;
        this.filteredParams.categories = categories;
    };

    @action
    clearFilteredParams = (): void => {
        this.showOnlyFavorite = false;
        this.filteredParams = {
            categories: [],
            favorite: false,
            find: '',
            provider: []
        };
        this.saveProviderFilter([]);
    };

    @action
    restoreSavedProviderFilter = (): boolean => {
        if (this.filteredParams.find || this.filteredParams.game_id) {
            return false;
        }

        const providerIds = this.readSavedProviderFilter();

        if (providerIds.length === 0) {
            return false;
        }

        this.filteredParams.provider = providerIds;
        return true;
    };

    readSavedProviderFilter = (): string[] => {
        if (typeof window === 'undefined') {
            return [];
        }

        const savedFilter = window.localStorage.getItem(this.providerFilterStorageKey);

        if (!savedFilter) {
            return [];
        }

        try {
            const parsedFilter = JSON.parse(savedFilter);
            const providerIds = Array.isArray(parsedFilter) ? parsedFilter : [parsedFilter];

            return providerIds.map(String).filter(Boolean);
        } catch {
            return savedFilter.split(',').filter(Boolean);
        }
    };

    saveProviderFilter = (providerIds: string[]): void => {
        if (typeof window === 'undefined') {
            return;
        }

        if (providerIds.length === 0) {
            window.localStorage.removeItem(this.providerFilterStorageKey);
            return;
        }

        window.localStorage.setItem(this.providerFilterStorageKey, JSON.stringify(providerIds));
    };

    @action
    clearSelectedCategories = (): void => {
        this.selectedCategoriesIds = [];
    };

    @action
    clearAllFilters = (): void => {
        this.clearFilteredParams();
        this.clearSelectedCategories();
        this.selectFilterGroup(ELEMENT_NOT_FOUND);
        this.changeSelectSorting(DEFAULT_SORTING);
    };

    @action
    changeActiveGame = (casinoGame: CasinoGameInterface | null): void => {
        this.activeCasinoGame = casinoGame;
    };

    @action
    changeIsGameActive = (isActive: boolean): void => {
        this.isGameActive = isActive;
    };

    @action
    setIframeGridPlacement = (placement: IframeGridPlacement | null): void => {
        this.iframeGridPlacement = placement;
    };

    @action
    changeLoadStatus = (status: boolean): void => {
        this.loadingNewGames = status;
    };

    @action
    clearFind = (): void => {
        this.filteredParams.find = '';
        this.filteredParams.game_id = null;
    };

    @action
    changeReloadPage = (reload: boolean): void => {
        this.reloadPage = reload;
    };

    @action
    updateGamesByFilters(item: CasinoGameInterface): void {
        this.casinoFilters.forEach(filter => {
            filter.casinoGames[filter.casinoGames.findIndex(game => game.id === item.id)] = item;
        });
    }

    @action
    updateHomePageGame(item: CasinoGameInterface): void {
        this.homePageGames[this.homePageGames.findIndex(game => game.id === item.id)] = item;
    }

    @action
    updateFilters(items: CasinoFilterInterface): void {
        this.casinoFilters = [];
        this.casinoFilters = this.casinoFilters.concat(items);
    }

    @action
    updateFiltersGamesCollection(items: CasinoGamesStoreInterface, filterId: number): void {
        const casinoFilter = this.casinoFilters.find(filter => filter.id === filterId);
        const filteredGames =
                items.casinoGames.filter(game => game.devices.includes(this.device));
        casinoFilter.casinoGames = casinoFilter.casinoGames.concat(filteredGames);
    }

    @action
    updateGameLaunchOptions(params?: GameLaunchOptionsInterface): void {
        this.gameLaunchOptions = params;
    }

    loadCasinoGames = (filtered?: FilteredParamsInterface): void => {
        const params = filtered || this.filteredParams;
        const queryParams = {
            filter_group: this.selectedFilterGroupId > 0 ? this.selectedFilterGroupId : null,
            order_by: this.sortingValue,
            page: this.partsLoaded + 1,
            ...params
        };
        this.updateLastFind({...this.filteredParams});
        loadGames(
            queryParams,
            this.savePartCasinoGames
        );
    };

    savePartCasinoGames = (data: CasinoGamesStoreInterface): void => {
        const oldPart = this.data.casinoGames || [];
        const newPart = data.casinoGames || [];
        const mergedParts = [...oldPart, ...newPart];
        const partsCasinoGames = Array.from(new Map(mergedParts.map(game => [game.id, game])).values());
        this.update('data', {
            ...this.data,
            ...{casinoGames: partsCasinoGames}
        });
        this.updateCasinoGamesCategories(data.casinoGamesCategories);
        this.updateCasinoProviders(data.casinoProviders);
        this.changeLoadStatus(false);
        this.changeHasMore(data.has_more);
        this.partsChanged(this.partsLoaded + 1);
        if (data.random_slot_games?.length) {
            this.setRandomSlotGames(data.random_slot_games);
        }
    };

    playGame = (params: PlayGameParamsInterface): boolean => {
        const filter = this.casinoFilters.find(f => f.id === this.selectedFilterGroupId) || params.filter;
        const activeGame = casinoGameById(params.id, params.filter ? params.filter.id : null);

        if (!activeGame) {
            toastError(I18n.t('server_error_try_later'));
            return false;
        }

        if (activeGame.restricted) {
            toastError(I18n.t('slot_not_available'));
            return false;
        }
        this.updateGameLaunchOptions(null);
        this.changeActiveGame(activeGame);
        sendRequestStartCasino(
            `/casino/games/${params.id}/${params.demo ? 'launch_demo' : 'launch_play'} `,
            this.device
        );
        const favorite = this.showOnlyFavorite ? 'Favorite' : 'All Games';
        const filter_name = filter ? filter.name : favorite;

        logEvent('CASINO_GAME_OPENED', {
            SOURCE: this.filteredParams.find ? 'Search' : filter_name,
            demo: params.demo,
            game: activeGame.name,
            game_type: activeGame.category,
            provider: activeGame.provider_name
        });
        return true;
    };

    closeModal = (): void => {
        if (this.isGameActive) {
            this.changeActiveGame(null);
            this.updateGameLaunchOptions(null);
        }
        this.changeIsGameActive(false);
        this.setIframeGridPlacement(null);
    };

    openModal = (): void => {
        this.changeIsGameActive(true);
        this.changeReloadPage(true);
    };

    activeGameToFavorite = (): void => {
        const {id, favorite} = this.activeCasinoGame;
        favoriteGameChange(id, !favorite, null);
    };

    selectProviders = (ids: string[]): void => {
        this.updateSelectProviders(ids);
        this.checkUpdateFilteredParams();
    };

    selectCategories = (ids: string[]): void => {
        const currentCategories = [];
        ids.forEach((id: number | string) => {
            currentCategories.push(
                this.casinoGamesCategories.find(item => item.id === Number(id)).name
            );
        });
        this.clearFind();
        this.updateSelectCategories(currentCategories, ids);
        this.checkUpdateFilteredParams();
    };

    changeSelectSorting = (value: string): void => {
        this.changeSortingValue(value);
        this.updateShowOnlyFavorite(false);
        this.clearFind();
        this.checkUpdateFilteredParams(true);
    };

    changeFilterGroup = (id: number): void => {
        this.selectFilterGroup(id);
        this.updateShowOnlyFavorite(false);
        this.clearFilteredParams();
        this.clearSelectedCategories();
        this.checkUpdateFilteredParams(true);
    };

    checkUpdateFilteredParams = (changeSorting?: boolean): void => {
        if (lastFindSame() && !changeSorting) {
            return;
        }

        this.partsChanged(0);
        this.updateGamesCollection([]);
        this.changeLoadStatus(true);
        this.setFilteredParams();
    };

    setFilteredParams = (): void => {
        const {find, provider, game_id} = this.filteredParams;

        if (find !== '') {
            this.loadCasinoGames({find, game_id, provider});
            this.clearFilterGroup();
            return;
        }
        if (this.showOnlyFavorite) {
            this.clearFilterGroup();
        } else {
            this.defaultFilteredParams();
        }
        this.loadCasinoGames();
    };

    clearFilterGroup = (): void => {
        this.selectFilterGroup(null);
        this.changeSortingValue('');
    };

    defaultFilteredParams = (): void => {
        if (!this.selectedFilterGroupId) {
            this.selectFilterGroup(ELEMENT_NOT_FOUND);
        }
        if (this.sortingValue === '') {
            this.changeSortingValue(DEFAULT_SORTING);
        }
    };

    liveSearchApiService = (inputValue: string): Promise<Response> => new RestApiService('/casino/games/load_games')
        .index({
            device: this.device,
            favorite: false,
            filter_group: '',
            find: inputValue,
            game_id: this.filteredParams.game_id,
            limit: GAMES_FIND_LIMIT,
            provider: this.filteredParams.provider
        });

    showFilterGroups = (): boolean => this.selectedFilterGroupId === ELEMENT_NOT_FOUND &&
            this.selectedCategoriesIds.length === 0 && this.filteredParams.provider.length === 0;

    listenCasinoGameUpdate(payload: CasinoGameUpdatePayloadInterface): void {
        this.update('data', {
            ...this.data,
            usersFavoritesGameCount: payload.usersFavoritesGameCount
        });
        this.setFavoriteGamesCount(payload.usersFavoritesGameCount);
        this.updateGamesByFilters(payload.casino_game);
        this.updateElementCollection(payload.casino_game);
        this.updateHomePageGame(payload.casino_game);
        this.updatePageElementsGame(payload.casino_game);
    }

    listenCasinoSession(payload: CasinoLaunchInterface): void {
        if (payload.errors) {
            toastError(payload.errors.base[0]);
            this.closeModal();
        } else {
            this.updateGameLaunchOptions({
                launch_url: payload.launch_url,
                target_element: 'a8r_iframe'
            });
        }
    }

    @action
    updateHomePageGames(casinoGames: CasinoGameInterface[]): void {
        this.homePageGames = casinoGames;
    }
}
